import { useParams, Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, User, Play, Star } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { tools } from '../data/tools';
import { toolDetails } from '../data/toolDetails';

const ToolDetailPage = () => {
  const { id } = useParams();
  const { theme } = useTheme();
  const tool = tools.find(t => t.id === parseInt(id));
  const details = toolDetails[id];

  if (!tool || !details) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[var(--text-main)] mb-4">Tool Not Found</h1>
          <Link to="/services/tool-demos" className="text-[var(--accent-cyan)] hover:underline">Back to Tool Demos</Link>
        </div>
      </div>
    );
  }

  const handleWatch = () => {
    toast.success(`Starting ${tool.name} demo series`, { description: 'Loading video player...' });
  };

  return (
    <div className="min-h-screen pt-20" data-testid="tool-detail-page">
      <Toaster position="top-right" theme={theme} toastOptions={{ style: { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-main)' } }} />

      {/* Hero */}
      <section className="py-10 md:py-16 relative overflow-hidden border-b border-[var(--border)]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-main relative z-10">
          <Link to="/services/tool-demos" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Tool Demos
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs text-[var(--accent-cyan)] font-mono uppercase">{tool.category}</span>
                <span className={`px-2 py-1 text-xs font-mono ${
                  tool.difficulty === 'Beginner' ? 'bg-[var(--primary)]/20 text-[var(--primary)]' :
                  tool.difficulty === 'Intermediate' ? 'bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)]' :
                  tool.difficulty === 'Advanced' ? 'bg-[var(--accent-purple)]/20 text-[var(--accent-purple)]' :
                  'bg-[var(--error)]/20 text-[var(--error)]'
                }`}>{tool.difficulty}</span>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-[#FFD700] fill-[#FFD700]" />
                  <span className="text-sm text-[var(--text-main)]">{tool.rating}</span>
                </div>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)] mb-6">{tool.name}</h1>
              <p className="text-[var(--text-muted)] text-lg leading-relaxed mb-6">{details.summary}</p>
              <div className="flex flex-wrap gap-2">
                {tool.tags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 bg-[var(--accent-cyan)]/10 border border-[var(--accent-cyan)]/30 text-sm text-[var(--accent-cyan)] font-mono">{tag}</span>
                ))}
              </div>
            </div>
            <div className="bg-[var(--surface)] border border-[var(--border)] p-6">
              <div className="space-y-4 mb-6">
                <div><p className="text-xs text-[var(--text-muted)]">Demo Videos</p><p className="font-mono text-4xl font-bold text-[var(--accent-cyan)]">{tool.demos}</p></div>
                <div><p className="text-xs text-[var(--text-muted)]">Difficulty</p><p className="text-[var(--text-main)] font-mono">{tool.difficulty}</p></div>
                <div><p className="text-xs text-[var(--text-muted)]">Category</p><p className="text-[var(--text-main)] font-mono">{tool.category}</p></div>
              </div>
              <div className="border-t border-[var(--border)] pt-6">
                <p className="font-mono text-xl font-bold text-[var(--primary)]">Included in Training</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./demo_topics</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {details.topics.map((topic, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-[var(--surface)] border border-[var(--border)]">
                <Play className="w-5 h-5 text-[var(--accent-cyan)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{topic}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./ideal_for</p>
          <ul className="space-y-3">
            {details.suitableFor.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <User className="w-5 h-5 text-[var(--accent-cyan)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./what_you_will_learn</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {details.benefits.map((benefit, idx) => (
              <div key={idx} className="p-4 bg-[var(--surface)] border border-[var(--border)]">
                <CheckCircle className="w-6 h-6 text-[var(--primary)] mb-3" />
                <p className="text-[var(--text-main)]">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-8 md:py-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="container-main relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-main)] mb-4">Ready to Master {tool.name}?</h2>
          <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto mb-8">Access all {tool.demos} demo videos and start learning today.</p>
          <button onClick={handleWatch} className="bg-[var(--primary)] text-black font-mono font-bold px-12 py-4 text-lg uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_25px_var(--glow-primary)]" data-testid="watch-btn">Watch Demos</button>
        </div>
      </section>
    </div>
  );
};

export default ToolDetailPage;
